import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import type {
  FormationManagementFacets,
  GeologicIntervalListQueryResponse,
} from 'types/api-responses';

/** Request body for POST /queries/geologic_intervals (snake_case date keys match Rails `query_params`). */

export interface FormationManagementSearchParams extends SearchParams {
  basins?: string[];
  intervals?: string[];
  creators?: string[];
  created_at?: { start?: string; end?: string };
  updated_at?: { start?: string; end?: string };
}

/**
 * Initial query params from the current URL (used by both the search atom
 * default payload and the DataTable-driven `useState` in the page).
 *
 * @param {number} [defaultPageSize] - Default rows per page when URL has no `page_size`.
 * @return {FormationManagementSearchParams} Parsed search params.
 */
export function getFormationManagementInitialParams(
  defaultPageSize = 25
): FormationManagementSearchParams {
  const params = new URLSearchParams(window.location.search);

  const basin = params.get('basin') || undefined;
  const interval = params.get('interval') || undefined;
  const creator = params.get('creator') || undefined;
  const createdFrom = params.get('created_from') || undefined;
  const createdTo = params.get('created_to') || undefined;
  const updatedFrom = params.get('updated_from') || undefined;
  const updatedTo = params.get('updated_to') || undefined;

  const createdAtRange =
    createdFrom || createdTo
      ? { start: createdFrom, end: createdTo }
      : undefined;
  const updatedAtRange =
    updatedFrom || updatedTo
      ? { start: updatedFrom, end: updatedTo }
      : undefined;

  const rawSortDirection = params.get('sort_direction');
  const sortDirection: 'ASC' | 'DESC' | undefined =
    rawSortDirection === 'ASC' || rawSortDirection === 'DESC'
      ? rawSortDirection
      : undefined;

  return {
    page: parseInt(params.get('page') || '1', 10),
    pageSize: parseInt(params.get('page_size') || String(defaultPageSize), 10),
    text: params.get('text') || undefined,
    basins: basin ? [basin] : undefined,
    intervals: interval ? [interval] : undefined,
    creators: creator ? [creator] : undefined,
    created_at: createdAtRange,
    updated_at: updatedAtRange,
    sortAttribute: params.get('sort_attribute') || undefined,
    sortDirection,
    paginated: true,
  };
}

export const createFormationManagementGeologicIntervalsSearchAtom = () =>
  searchAtomPair<
    GeologicIntervalListQueryResponse,
    FormationManagementSearchParams,
    FormationManagementFacets
  >({ href: '/queries/geologic_intervals' });
